import type { ReactNode } from 'react';
import { Button, Card, Typography } from 'antd';
import { AsyncBoundary, space, typeScale } from '@viq-admin/ui';

/**
 * One Overview card. The heading is a button that opens the card's tab, so
 * "show me all of them" is one click from the summary. Loading is the
 * Card's own skeleton (AsyncBoundary's table/form skeletons are page-sized);
 * errors go through AsyncBoundary as everywhere else.
 */
export function SummaryCard({
  title,
  onOpen,
  loading,
  error,
  onRetry,
  testId,
  children,
}: {
  title: string;
  onOpen: () => void;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <Card
      size="small"
      data-testid={testId}
      loading={loading && !error}
      title={
        <Button
          type="link"
          onClick={onOpen}
          style={{ padding: 0, height: 'auto', fontWeight: 600 }}
        >
          {title} →
        </Button>
      }
      style={{ minWidth: 0 }}
    >
      <AsyncBoundary error={error} onRetry={onRetry}>
        {children}
      </AsyncBoundary>
    </Card>
  );
}

/** A big number over a short label; a row of these wraps on a narrow card. */
export function Metric({
  value,
  label,
  tone,
}: {
  value: number;
  label: string;
  /** Colour the number when it needs attention (e.g. stranded installs). */
  tone?: 'danger' | 'warning' | 'success';
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <Typography.Text
        strong
        type={tone}
        style={{ display: 'block', fontSize: typeScale.h4, lineHeight: 1.2 }}
      >
        {value}
      </Typography.Text>
      <Typography.Text type="secondary">{label}</Typography.Text>
    </div>
  );
}

export function MetricRow({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.lg, marginBottom: space.md }}>
      {children}
    </div>
  );
}
